import { FC, useMemo } from "react";
import {
  BarChart as RechartsHorizontalBarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import { GridLegacy as Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardFooter } from "./common/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
} from "./common/chart-config";
import { CSVDownloader } from "../fileDownload/csv-downloader";
import { IChartOptions, isChartOptions } from "./common/chart-options";
import { IChartProps } from "./common/types";
import { useUserParams } from "../../context/user-params-context";
import { formatLabel } from "./common/format-helpers";
import { ChartInfoTooltip } from "./common/card-info-tooltip";
import { useChartData } from "../../hooks/useChartData";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";
import { resolveChartTitle } from "./common/resolve-chart-title";
import { usePreferences } from "../../utility/usePreferences";
import { capitalise } from "../../utility/utils";

interface QueryResultItem {
  name: string;
  value: number;
  barId: string;
}

interface BarOptions {
  fill?: string;
  name?: string;
}

interface IHorizontalBarChartOptions extends IChartOptions {
  bar?: Record<string, BarOptions>;
  isStacked?: boolean;
}

export const HorizontalBarChart: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const { userParams, parameterTypes } = useUserParams();
  const { distancePerHour, distanceUnitText, distanceUnitSingular } = usePreferences();

  const { data, isLoading } = useChartData<
    QueryResultItem[],
    IHorizontalBarChartOptions
  >(uuid, parameterTypes, userParams, { validateOptions: isChartOptions });

  const releaseRefactorChartTitle = useReleaseManagement(
    ReleaseFlag.releaseRefactorChartTitle
  );

  const chartOptions = data?.options;
  const chartData = data?.data || [];
  const chartName = data?.name;
  const chartTranslationKey = data?.translationKey ?? undefined;

  const chartConfig: ChartConfig = Object.entries(
    chartOptions?.bar || {}
  ).reduce((acc, [key, barOptions]) => {
    acc[key] = {
      label: barOptions.name || key,
      color: barOptions.fill || `#8884d8`,
    };
    return acc;
  }, {} as ChartConfig);

  const computedTitle = useMemo(() => {
    return resolveChartTitle({
      t,
      name: chartName ?? undefined,
      translationKey: chartTranslationKey,
      featureEnabled: releaseRefactorChartTitle,
      defaultText: chartName ?? undefined,
      i18nParams: {
        distancePerHour,
        distanceUnit: capitalise(distanceUnitText),
        distanceUnitSingular: capitalise(distanceUnitSingular),
        timeFormat: "hh:mm",
      },
    });
  }, [
    t,
    chartName,
    chartTranslationKey,
    releaseRefactorChartTitle,
    distancePerHour,
    distanceUnitText,
    distanceUnitSingular,
  ]);

  return (
    <Grid item xs={12} md={8} lg={6}>
      <Card
        style={{
          animation: isLoading ? "pulse 2s linear infinite" : undefined,
        }}
      >
        {chartOptions?.description && (
          <ChartInfoTooltip description={chartOptions.description} />
        )}
        <CardContent>
          <ChartContainer config={chartConfig}>
            <RechartsHorizontalBarChart layout="vertical" data={chartData}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                width={100}
                axisLine={false}
                tickLine={false}
                dataKey={chartOptions?.yAxis?.dataKey || "name"}
                label={{
                  value: formatLabel(userParams, chartOptions?.yAxis?.label),
                  angle: chartOptions?.yAxis?.angle || -90,
                  position: "insideLeft",
                }}
                tickFormatter={(value) =>
                  formatLabel(userParams, {
                    ...chartOptions?.yAxis?.tick?.label,
                    value,
                  })
                }
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    heading={chartOptions?.tooltip?.heading}
                    nameKey={chartOptions?.tooltip?.nameKey}
                    labelFormatter={(value) =>
                      formatLabel(userParams, {
                        ...chartOptions?.tooltip?.label,
                        value,
                      })
                    }
                  />
                }
              />
              {chartOptions?.showLegend && (
                <ChartLegend
                  verticalAlign="top"
                  align="right"
                  layout="vertical"
                />
              )}
              <Bar
                dataKey={chartOptions?.dataKey || "value"}
                radius={[0, 4, 4, 0]}
              >
                {chartData.map((item) => (
                  <Cell
                    key={`cell-${item.barId}`}
                    fill={
                      chartOptions?.bar?.[item.barId]?.fill || "#8884d8"
                    }
                  />
                ))}
              </Bar>
            </RechartsHorizontalBarChart>
          </ChartContainer>
        </CardContent>
        <CardFooter>
          <Typography variant="subtitle1">
            {isLoading ? t("general.loading") : computedTitle}
          </Typography>
          {chartOptions?.enableCSVDownload && (
            <CSVDownloader data={chartData} filename={chartName || ""} />
          )}
        </CardFooter>
      </Card>
    </Grid>
  );
};

export default HorizontalBarChart;
